/**
 * Reicuri AI Brain — Fully Offline, API-Free
 * Built from the official brand documents in the zip file.
 * Uses keyword & intent matching to answer customer questions.
 */

window.ReicuriBrain = (() => {

  // ============================================================
  // KNOWLEDGE BASE — Built directly from Leadvate zip file docs
  // ============================================================
  const KB = {
    brand: {
      name: "Reicuri Pvt Ltd",
      tagline: "Pure Wellness. Proven Science.",
      address: "Plot No 32, 2nd floor, 3rd Cross, Dasarahalli Main Rd, Balaji Layout, Rachenahalli, Bengaluru, Karnataka 560024",
      email: "digital@reicuri.com",
      phone: "98458 15772",
      website: "reicuri.com",
    },

    products: {
      "omega3-capsules": {
        handle: "reicuri-paradox-omega-3-capsules",
        name: "Reicuri Paradox Omega 3 Capsules",
        keywords: ["omega 3 capsule", "omega3 capsule", "paradox capsule", "omega capsule", "fish oil capsule"],
        tagline: "Most stable Omega in Triglyceride form",
        highlights: [
          "Most stable Omega (Triglyceride form)",
          "Patented formula by Dr. Hayhurst, Ulster University (UK & Ireland)",
          "Deep sea Icelandic fish oil with Mediterranean olive oil and lemon oil",
          "Up to 70% better absorption than ethyl ester omega-3",
          "No fishy burps — lemon oil infused",
          "Heavy Metal-Free — third-party tested",
          "FSSAI Approved",
          "Non-GMO & Gluten-Free",
        ],
        ingredients: "Pure Omega-3 Fish Oil (EPA 160mg + DHA 105mg per capsule), Natural Lemon Oil, Mediterranean Olive Oil, Gelatin Capsule",
        benefits: [
          "Supports Heart Health — lowers triglycerides, prevents plaque buildup",
          "Boosts neurological development and memory retention",
          "Improves eyesight and vision",
          "Boosts immunity and cognition",
          "Nourishes skin and hair",
          "Reduces inflammation and joint pain",
          "Supports healthy blood flow and stabilizes heart rhythms",
          "Helps with PMS and menstrual discomfort",
          "Supports female hormonal balance and fertility",
          "Beneficial during menopause (reduces hot flashes, mood changes)"
        ],
        usage: "Take 1–2 capsules daily with meals.",
        serving: "1–2 capsules per day. Each capsule: EPA 160mg + DHA 105mg",
        warnings: [
          "Not for medicinal use",
          "Consult a doctor if pregnant, nursing, or on blood thinners",
          "Not a substitute for a balanced diet",
          "Store in a cool, dry place away from sunlight",
          "Keep out of reach of children"
        ],
        storage: "Cool, dry place away from direct sunlight.",
        who_should_use: "Adults seeking heart health, brain health, joint support, hormonal balance, skin & hair health.",
        who_should_avoid: "People with fish or shellfish allergies. Consult a doctor if on blood-thinning medication.",
        related: ["omega3-liquid"],
        faq: [
          { q: "Why is it better than other omega-3 supplements?", a: "Reicuri Paradox Omega-3 uses a patented triglyceride form — the natural form found in fish — providing up to 70% better absorption than cheaper ethyl ester forms used in most supplements." },
          { q: "Will it cause fishy burps?", a: "No. Thanks to the lemon oil infusion, there are absolutely no fishy burps or aftertaste." },
          { q: "Can it reduce heart attack risk?", a: "Regular use may help reduce the risk of heart attack and stroke by lowering triglycerides, preventing plaque buildup, reducing inflammation, and supporting healthy blood flow." },
          { q: "Is it good for women's hormonal health?", a: "Yes. EPA and DHA help regulate hormone production, support smoother menstrual cycles, reduce PMS symptoms, and support fertility and menopausal transitions." },
          { q: "What is the difference between the capsule and liquid?", a: "Capsules contain EPA 160mg + DHA 105mg per capsule. The liquid form contains EPA 678mg + DHA 430mg per serving — equivalent to 3–4 servings of fatty fish — making it a stronger daily dose." }
        ]
      },

      "omega3-liquid": {
        handle: "reicuri-paradox-omega-3-liquid-fish-oil",
        name: "Reicuri Paradox Omega 3 Liquid",
        keywords: ["omega 3 liquid", "omega3 liquid", "paradox liquid", "fish oil liquid", "omega liquid"],
        tagline: "Fast-absorbing liquid Omega-3 — strongest dose available",
        highlights: [
          "Liquid form for faster absorption",
          "EPA 678mg + DHA 430mg per serving — equivalent to 3–4 servings of fatty fish",
          "Natural Lemon flavour — no fishy taste",
          "Same patented formula as the capsules"
        ],
        ingredients: "Pure Omega-3 Fish Oil (EPA 678mg + DHA 430mg per 5ml serving), Natural Lemon Flavor, Vitamin E",
        benefits: [
          "Superior heart health support with the highest EPA/DHA dose",
          "Fast absorption — liquid bypasses slow digestion",
          "Supports brain health, memory, and cognition",
          "Excellent for joint pain and inflammation",
          "Great for skin, hair, and immunity"
        ],
        usage: "Take 5ml (1 teaspoon) daily, preferably with a meal.",
        serving: "5ml per day. Contains EPA 678mg + DHA 430mg.",
        warnings: [
          "Shake well before use",
          "Consult a doctor if pregnant or on medication",
          "Not a substitute for a balanced diet"
        ],
        storage: "Refrigerate after opening. Consume within 2 months of opening.",
        who_should_use: "Adults and teenagers who prefer liquid supplements, or those needing a higher Omega-3 dose.",
        who_should_avoid: "People with fish or shellfish allergies.",
        related: ["omega3-capsules", "omega3-junior"],
        faq: [
          { q: "How does it compare to the capsules?", a: "The liquid contains EPA 678mg + DHA 430mg per serving vs EPA 160mg + DHA 105mg in the capsule — making it significantly more potent. Ideal for those wanting maximum benefit." },
          { q: "Does it taste bad?", a: "No. It is naturally lemon-flavoured and there is no fishy aftertaste." }
        ]
      },

      "omega3-junior": {
        handle: "reicuri-paradox-omega-3-junior-liquid",
        name: "Reicuri Paradox Omega 3 Junior Liquid",
        keywords: ["junior omega", "omega junior", "children omega", "kids omega", "omega 3 junior", "child supplement", "children supplement"],
        tagline: "Premium Omega-3 formulated for growing children",
        highlights: [
          "Specially formulated for children aged 3–12",
          "Mixed Berry flavour — kids love it",
          "Contains Vitamin D3 for bone health",
          "Supports brain development and vision"
        ],
        ingredients: "Purified Omega-3 Fish Oil, Natural Mixed Berry Flavour, Vitamin D3",
        benefits: [
          "Supports healthy brain development and learning",
          "Improves vision and eye health",
          "Strengthens immunity",
          "Supports bone development (with Vitamin D3)"
        ],
        usage: "Children 3–6 years: 2.5ml daily. Children 7–12 years: 5ml daily.",
        serving: "2.5ml to 5ml per day depending on age.",
        warnings: [
          "Keep out of reach of very young children",
          "Do not exceed recommended dosage",
          "Contains fish — not for children with fish allergy",
          "Consult a paediatrician for children under 3 years"
        ],
        storage: "Refrigerate after opening.",
        who_should_use: "Children aged 3 to 12 years.",
        who_should_avoid: "Infants under 3 years without medical advice. Children with fish allergies.",
        related: ["omega3-liquid"],
        faq: [
          { q: "From what age can my child take this?", a: "It is suitable for children from 3 years and older. For younger children, always consult a paediatrician first." },
          { q: "Will my child like the taste?", a: "Yes! It has a natural mixed berry flavour that children enjoy." }
        ]
      },

      "manviox-q10": {
        handle: "marnys-manviox-q10-liquid-supplement",
        name: "Manviox Q10 (Royal Q10)",
        keywords: ["q10", "royal q10", "manviox", "coq10", "coenzyme q10", "manviox q10"],
        tagline: "Cellular energy booster and powerful antioxidant",
        highlights: [
          "Contains Coenzyme Q10 (Ubiquinone) — essential for cellular energy",
          "Contains Royal Jelly Extract — a rare and powerful natural compound",
          "Powerful antioxidant that protects cells from oxidative stress",
          "Supports heart health and reduces fatigue"
        ],
        ingredients: "Coenzyme Q10 (Ubiquinone), Royal Jelly Extract, Vitamin B Complex",
        benefits: [
          "Boosts cellular energy production (ATP)",
          "Acts as a powerful antioxidant — fights free radicals",
          "Supports heart muscle function and cardiovascular health",
          "Reduces fatigue and improves stamina",
          "May support skin health and anti-aging"
        ],
        usage: "Take 1 capsule daily, preferably in the morning with a meal.",
        serving: "1 capsule per day.",
        warnings: [
          "Avoid taking late in the day as it may affect sleep",
          "Contains bee products (Royal Jelly) — avoid if allergic to bees",
          "Consult a healthcare professional if on blood pressure medication"
        ],
        storage: "Store in a cool, dark place below 25°C.",
        who_should_use: "Adults experiencing fatigue, those wanting cardiovascular support, or looking for antioxidant protection.",
        who_should_avoid: "People allergic to bee products.",
        related: ["b12-rise"],
        faq: [
          { q: "What makes Royal Jelly special?", a: "Royal Jelly is a rare compound produced by bees, known for its potential to support energy, immunity, and longevity. Combined with CoQ10, it creates a powerful energy and antioxidant formula." },
          { q: "Will it help with fatigue?", a: "Yes. CoQ10 is essential for mitochondrial energy production. Low CoQ10 levels are directly linked to fatigue, and supplementing helps restore energy at the cellular level." }
        ]
      },

      "artrohelp": {
        handle: "artrohelp-forte",
        name: "Artrohelp Forte",
        keywords: ["artro", "artrohelp", "joint", "joint support", "arthritis", "cartilage", "glucosamine", "knee pain", "joint pain"],
        tagline: "Complete joint support for flexibility and comfort",
        highlights: [
          "Contains Glucosamine Sulfate + Chondroitin + MSM + Boswellia",
          "Supports cartilage repair and regeneration",
          "Reduces joint inflammation",
          "Improves joint flexibility and mobility"
        ],
        ingredients: "Glucosamine Sulfate, Chondroitin, MSM (Methylsulfonylmethane), Boswellia Extract, Vitamin C",
        benefits: [
          "Promotes joint flexibility and range of motion",
          "Reduces inflammation and swelling in joints",
          "Helps in cartilage repair and protection",
          "Reduces joint pain and discomfort",
          "Supports long-term joint health for active individuals"
        ],
        usage: "Take 2 tablets daily with water, preferably with meals.",
        serving: "2 tablets per day.",
        warnings: [
          "May contain traces of shellfish (Glucosamine source)",
          "Consult a doctor if you have kidney issues",
          "Not recommended for children"
        ],
        storage: "Store at room temperature in a dry place.",
        who_should_use: "Athletes, active individuals, elderly people, or anyone experiencing joint discomfort, stiffness, or reduced mobility.",
        who_should_avoid: "People with severe shellfish allergies. Not for children.",
        related: ["omega3-capsules"],
        faq: [
          { q: "How long before I feel results?", a: "Most users report improvements in joint comfort and flexibility within 4 to 6 weeks of consistent daily use." },
          { q: "Is it good for athletes?", a: "Absolutely. Artrohelp Forte is designed to protect cartilage under the stress of regular physical activity and speed up joint recovery." }
        ]
      },

      "b12-rise": {
        handle: "reicuri-b12-rise",
        name: "Reicuri B12 Rise",
        keywords: ["b12", "vitamin b12", "b12 rise", "b12 shot", "b12 liquid", "energy shot", "fatigue", "b12 deficiency"],
        tagline: "High-Absorption Vitamin B12 Liquid Shot — Better absorption, sufficient energy",
        highlights: [
          "Liquid shot format for rapid mucosal absorption — faster than tablets or capsules",
          "Wild Berry flavour — delicious and easy to take",
          "100% Indian RDA of B12 per 1ml serving",
          "Naturally sweetened with Monk Fruit Extract — no artificial sweeteners",
          "GMP manufactured — no artificial flavours",
          "4 x 10ml vials per pack — 40 servings"
        ],
        ingredients: "Vitamin B12 (Cyanocobalamin), Glycerin, Sodium Benzoate, Potassium Sorbate, Citric Acid, Monk Fruit Extract, Natural Wild Berry Flavour",
        nutrition: "Per 1ml: Vitamin B12: 2.2mcg (100% RDA), Energy: 0.52 kcal, Protein: 0g, Carbohydrates: 0.13g, Fat: 0g",
        benefits: [
          "Natural energy production — combats fatigue and tiredness",
          "Reduces brain fog and enhances mental clarity",
          "Supports healthy red blood cell formation",
          "Vital for DNA synthesis",
          "Supports nerve integrity and neurological wellness",
          "Improves metabolism and nutrient conversion",
          "Gentle on the digestive system"
        ],
        usage: "Shake well. Take 1ml daily orally, or as advised by your healthcare professional.",
        serving: "1ml per day. Each pack contains 4 x 10ml vials = 40 servings.",
        warnings: [
          "Not for medicinal use",
          "Do not exceed recommended daily usage",
          "Not a substitute for a balanced diet",
          "Store in a cool, dry place away from sunlight",
          "Keep out of reach of children",
          "Pregnant women or individuals on anticoagulants should consult a doctor"
        ],
        storage: "Cool, dry place away from direct sunlight.",
        who_should_use: "Vegetarians, vegans, elderly individuals, people with fatigue, brain fog, or B12 deficiency. Suitable for all ages including children.",
        who_should_avoid: "Those on anticoagulants or blood thinners without consulting a doctor first.",
        related: ["manviox-q10"],
        faq: [
          { q: "Why a liquid shot instead of a tablet?", a: "The liquid format promotes quick mucosal absorption directly into the bloodstream — bypassing the slower digestion process required for tablets, resulting in faster and better bioavailability." },
          { q: "Is it suitable for vegetarians?", a: "Yes! B12 Rise is specially formulated for vegetarians and vegans who often lack B12 from their diet." },
          { q: "How many servings per pack?", a: "Each pack contains 4 vials x 10ml = 40 servings at 1ml per day, giving you over a month's supply." }
        ]
      },

      "bcaa-glutamine": {
        handle: "marnys-bcaa-complet",
        name: "Maryns BCAA + Glutamine",
        keywords: ["bcaa", "glutamine", "bcaa glutamine", "maryns", "workout", "recovery", "muscle", "post workout", "pre workout"],
        tagline: "Stimulant-free recovery formula for faster muscle recovery",
        highlights: [
          "BCAA in 2:1:1 ratio (Leucine:Isoleucine:Valine) — the science-backed optimal ratio",
          "Contains L-Glutamine for gut and muscle recovery",
          "Electrolyte blend for rehydration",
          "Stimulant-free — no caffeine",
          "Can be taken during or after a workout"
        ],
        ingredients: "L-Leucine, L-Isoleucine, L-Valine (2:1:1 ratio), L-Glutamine, Electrolyte Blend (Sodium, Potassium, Magnesium)",
        benefits: [
          "Speeds up muscle recovery post-workout",
          "Prevents muscle breakdown (catabolism) during exercise",
          "Replenishes electrolytes lost through sweat",
          "Supports muscle protein synthesis",
          "Reduces muscle soreness",
          "L-Glutamine supports gut health and immune function"
        ],
        usage: "Mix 1 scoop (10g) with 250–300ml of water. Drink during or after a workout.",
        serving: "1 scoop (10g) per day, ideally during or post-workout.",
        warnings: [
          "Consult a healthcare provider if you have kidney or liver issues",
          "Not for use by individuals under 18 years of age",
          "Keep tub tightly sealed and away from moisture"
        ],
        storage: "Store in a cool, dry place. Keep tub tightly sealed.",
        who_should_use: "Athletes, gym-goers, bodybuilders, and fitness enthusiasts over 18 years.",
        who_should_avoid: "Individuals under 18 years. People with kidney or liver conditions without medical advice.",
        related: ["artrohelp"],
        faq: [
          { q: "Does this contain caffeine or stimulants?", a: "No. Maryns BCAA + Glutamine is completely stimulant-free, making it suitable for both morning and evening workouts." },
          { q: "When should I take it?", a: "For best results, mix 1 scoop with 250–300ml water and consume during or immediately after your workout." },
          { q: "What does the 2:1:1 BCAA ratio mean?", a: "It means 2 parts Leucine for every 1 part Isoleucine and 1 part Valine. This is the scientifically researched optimal ratio for maximizing muscle protein synthesis." }
        ]
      }
    },

    policies: {
      shipping: {
        keywords: ["shipping", "delivery", "ship", "deliver", "how long", "transit", "dispatch"],
        content: `**Shipping & Delivery Policy**

✅ **Free Shipping** across India on all orders — no minimum order value.

📦 **Order Processing:** Orders are typically processed within 1–2 business days.

🚚 **Estimated Delivery Times:**
- Metro cities (Bengaluru, Mumbai, Delhi, Chennai, Hyderabad, Kolkata): **2–4 business days**
- Tier 2 & Tier 3 cities: **4–7 business days**
- Remote/rural areas: **7–10 business days**

📍 Once your order is dispatched, you will receive a tracking ID via email or SMS to track your shipment in real-time.

For shipping queries, contact us at **digital@reicuri.com** or call **98458 15772**.`
      },

      refund: {
        keywords: ["refund", "return", "cancel", "cancellation", "exchange", "money back", "damaged", "defective", "wrong product"],
        content: `**Refund & Return Policy**

❌ **Order Cancellation:**
- You may cancel your order **any time before dispatch**.
- Once shipped, cancellation is not possible but you can **reject the delivery** when it arrives.
- To cancel: email **digital@reicuri.com** or call **98458 15772** immediately.

✅ **Refund Eligibility:**
- Refunds are provided for cancelled orders, returned products, or products received **damaged, defective, or incorrect**.
- Refund requests must be raised **within 7 days of delivery**.
- Shipping is free, so no shipping charges are deducted from refunds.

💰 **Refund Process:**
- Upon approval, refunds are processed within **7–10 business days** to your original payment method.

🔄 **Returns & Exchanges:**
- Returns accepted for **damaged, defective, or incorrect products only**.
- Products must be **unused and in original packaging**.
- Exchanges are subject to stock availability.

🚫 **Non-Refundable:**
- Products that are opened, used, or damaged by the customer.
- Promotional or gift items (unless defective).

**No Cancellation Fees** — Reicuri charges zero cancellation fees on pre-dispatch cancellations.

For returns, contact: **digital@reicuri.com** | **98458 15772**`
      },

      privacy: {
        keywords: ["privacy", "data", "personal information", "data protection", "gdpr"],
        content: `**Privacy Policy Summary**

Reicuri Pvt Ltd is committed to protecting your personal information.

🔒 **Data We Collect:** Name, contact details, shipping address, payment info, and browsing preferences.

📋 **How We Use It:** To process orders, provide customer support, improve our services, and send promotional content (you can opt out anytime).

🍪 **Cookies:** We use cookies to enhance your experience. You can control these through your browser settings.

🤝 **Data Sharing:** We never sell your data. We only share with trusted partners (payment processors, delivery services) strictly to fulfill your order.

👤 **Your Rights:** You can access, update, or request deletion of your data anytime by emailing **digital@reicuri.com**.

📞 Contact us at: **digital@reicuri.com** | **98458 15772**`
      },

      terms: {
        keywords: ["terms", "conditions", "terms and conditions", "eligibility", "legal", "governing law"],
        content: `**Terms & Conditions Summary**

- You must be **18 years or older** to purchase from Reicuri.
- All prices are in **INR**. Prices may change without prior notice.
- **Free shipping** is offered across all of India.
- Full payment is required at the time of placing an order.
- All content, logos, and trademarks on reicuri.com are owned by Reicuri Pvt Ltd.
- These terms are governed by **Indian law**.
- For detailed T&C, please visit reicuri.com or contact us at **digital@reicuri.com**.`
      }
    },

    contact: {
      keywords: ["contact", "support", "help", "reach", "customer care", "call", "email", "address", "location"],
      content: `**Contact Reicuri Support**

📧 **Email:** digital@reicuri.com
📞 **Phone:** 98458 15772
🏢 **Address:** Plot No 32, 2nd floor, 3rd Cross, Dasarahalli Main Rd, Balaji Layout, Rachenahalli, Bengaluru, Karnataka 560024
🌐 **Website:** reicuri.com

Our team is available to assist you with orders, product queries, returns, and more.`
    },

    goals: {
      "heart health": ["omega3-capsules", "omega3-liquid", "manviox-q10"],
      "brain health": ["omega3-capsules", "omega3-liquid", "b12-rise"],
      "energy": ["b12-rise", "manviox-q10"],
      "joint support": ["artrohelp", "omega3-capsules"],
      "recovery": ["bcaa-glutamine", "artrohelp"],
      "muscle growth": ["bcaa-glutamine"],
      "children": ["omega3-junior"],
      "general wellness": ["omega3-capsules", "b12-rise", "manviox-q10"],
      "fatigue": ["b12-rise", "manviox-q10"],
      "skin": ["omega3-capsules", "omega3-liquid"],
      "hormones": ["omega3-capsules", "omega3-liquid"],
      "women health": ["omega3-capsules", "omega3-liquid"],
      "immunity": ["omega3-capsules", "omega3-junior", "b12-rise"],
      "workout": ["bcaa-glutamine"],
      "vegetarian": ["b12-rise"],
      "vegan": ["b12-rise"],
    },

    greetings: ["hello", "hi", "hey", "namaste", "helo", "hii", "good morning", "good evening", "good afternoon", "sup", "what's up", "howdy"],

    fallback: `I'm here specifically to help you with information about Reicuri products and services. I don't have information about that topic.

**Here's what I can help you with:**
- 💊 **Products** — Omega-3 Capsules, Omega-3 Liquid, Junior Omega-3, Manviox Q10, Artrohelp Forte, B12 Rise, BCAA + Glutamine
- 🎯 **Find the right supplement** for your goal
- 🚚 **Shipping & delivery** information
- 🔄 **Refunds & returns** policy
- 📞 **Contact support**

Feel free to ask me anything about the above!`
  };

  // ============================================================
  // TF-IDF SEARCH ENGINE
  // ============================================================

  function tokenize(text) {
    if (!text) return [];
    const stopwords = new Set(["a","an","and","are","as","at","be","but","by","for","if","in","into","is","it","no","not","of","on","or","such","that","the","their","then","there","these","they","this","to","was","will","with","how","can","you","guys","do","does","what","why","where","when","which"]);
    return text.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(w => w.length > 1 && !stopwords.has(w));
  }

  class SearchIndex {
    constructor() {
      this.documents = [];
      this.df = {};
      this.N = 0;
    }

    addDocument(id, type, content, metadata = {}) {
      const tokens = tokenize(content);
      const tf = {};
      tokens.forEach(t => { tf[t] = (tf[t] || 0) + 1; });
      
      const uniqueTokens = new Set(tokens);
      uniqueTokens.forEach(t => { this.df[t] = (this.df[t] || 0) + 1; });
      
      this.documents.push({ id, type, tf, length: tokens.length, content, metadata });
      this.N++;
    }

    search(query) {
      const tokens = tokenize(query);
      if (tokens.length === 0) return null;

      let bestScore = 0;
      let bestDoc = null;

      this.documents.forEach(doc => {
        let score = 0;
        tokens.forEach(t => {
          if (doc.tf[t]) {
            const tf = doc.tf[t] / doc.length;
            const idf = Math.log(this.N / (this.df[t] || 1)) + 1;
            score += tf * idf;
          }
        });

        if (doc.type === 'product' && doc.metadata.keywords) {
          tokens.forEach(t => {
            if (doc.metadata.keywords.some(k => k.split(/\s+/).includes(t))) score += 1.5;
          });
        }
        if (doc.type === 'goal') {
          tokens.forEach(t => {
            if (doc.tf[t]) score += 2.0;
          });
        }

        if (score > bestScore) {
          bestScore = score;
          bestDoc = doc;
        }
      });

      return bestScore > 0.15 ? { doc: bestDoc, score: bestScore } : null;
    }
  }

  const index = new SearchIndex();

  Object.entries(KB.products).forEach(([id, p]) => {
    const pContent = `${p.name} ${p.tagline} ${p.keywords.join(' ')} ${Array.isArray(p.benefits) ? p.benefits.join(' ') : p.benefits} ${p.usage} ${p.serving}`;
    index.addDocument(id, 'product', pContent, { keywords: p.keywords, product: p });
    
    if (p.faq) {
      p.faq.forEach(faq => {
        index.addDocument(`${id}-faq-${faq.q}`, 'faq', `${faq.q} ${faq.a}`, { q: faq.q, a: faq.a, productId: id, product: p });
      });
    }
  });

  Object.entries(KB.policies).forEach(([id, pol]) => {
    index.addDocument(id, 'policy', pol.content + ' ' + pol.keywords.join(' '), { content: pol.content });
  });
  index.addDocument('contact', 'policy', KB.contact.content + ' ' + KB.contact.keywords.join(' '), { content: KB.contact.content });

  Object.entries(KB.goals).forEach(([goal, products]) => {
    index.addDocument(`goal-${goal}`, 'goal', goal, { goal, products });
  });

  function formatProductCard(product) {
    const p = typeof product === 'string' ? { ...KB.products[product], id: product } : product;
    if (!p) return '';
    return `
<div class="reicuri-product-card">
  <div class="reicuri-product-info">
    <h4 class="reicuri-product-title">${p.name}</h4>
    <p class="reicuri-product-desc">${Array.isArray(p.benefits) ? p.benefits[0] : p.benefits}</p>
    <div class="reicuri-product-actions">
      <button class="reicuri-btn-secondary" onclick="window.open(window.ReicuriContext ? window.ReicuriContext.shopUrl + '/products/${p.handle || p.id}' : '/products/${p.handle || p.id}', '_blank')">View Product</button>
    </div>
  </div>
</div>`;
  }

  function formatProductDetail(p) {
    let html = `**${p.name}**\n\n`;
    if (p.tagline) html += `_${p.tagline}_\n\n`;
    if (p.highlights) html += `✅ **Why it's special:**\n${p.highlights.map(h => `• ${h}`).join('\n')}\n\n`;
    if (p.benefits) html += `💊 **Key Benefits:**\n${(Array.isArray(p.benefits) ? p.benefits : [p.benefits]).map(b => `• ${b}`).join('\n')}\n\n`;
    if (p.usage) html += `📋 **How to use:** ${p.usage}\n\n`;
    if (p.serving) html += `⚖️ **Serving Size:** ${p.serving}\n\n`;
    if (p.who_should_use) html += `👤 **Best for:** ${p.who_should_use}\n\n`;
    if (p.warnings) html += `⚠️ **Cautions:** ${(Array.isArray(p.warnings) ? p.warnings.join(' | ') : p.warnings)}\n`;
    return html;
  }

  // ============================================================
  // MAIN CHAT HANDLER
  // ============================================================
  function respond(userMessage) {
    const q = userMessage.trim();
    const qLow = q.toLowerCase();

    if (KB.greetings.some(g => qLow.startsWith(g) || qLow === g)) {
      return {
        text: `Hello! 👋 Welcome to **Reicuri Wellness**.\n\nI'm your personal Reicuri AI Wellness Consultant. I can help you:\n• 🎯 **Find the right supplement** for your goal\n• 💊 **Learn about any of our products**\n• 🚚 **Check shipping & delivery** info\n• 🔄 **Understand our refund policy**\n• 📞 **Get in touch with our support team**\n\nWhat are you looking for today?`,
        cards: []
      };
    }

    if (/(find|suggest|recommend|what supplement|right supplement|help me choose|which product)/i.test(q)) {
      return {
        text: `I'd love to help you find the right supplement! What is your main health goal?`,
        options: [
          { label: "🫀 Heart Health", query: "Heart Health" },
          { label: "🧠 Brain Health", query: "Brain Health" },
          { label: "⚡ Energy & Fatigue", query: "Energy & Fatigue" },
          { label: "🦴 Joint Support", query: "Joint Support" },
          { label: "💪 Muscle Recovery", query: "Muscle Recovery" },
          { label: "👶 Children's Health", query: "Children" },
          { label: "🌿 General Wellness", query: "General Wellness" },
          { label: "👩 Women's Health", query: "Women health" }
        ],
        cards: []
      };
    }

    if (/(all products|show products|browse|product list|all supplements)/i.test(q)) {
      return {
        text: `Here are all the Reicuri products:`,
        cards: Object.keys(KB.products)
      };
    }

    if (/(track|order status|where is my order|tracking)/i.test(q)) {
      return {
        text: `**Track Your Order**\n\nOnce your order is dispatched, you will receive a **tracking ID via SMS or email**.\n\nYou can use that tracking ID on the courier partner's website to get real-time updates on your shipment.\n\n📧 If you haven't received a tracking ID or need help, please contact us:\n- **Email:** digital@reicuri.com\n- **Phone:** 98458 15772`,
        cards: []
      };
    }

    if (/(about reicuri|who are you|what is reicuri|about .* brand|about your company|who made|founded|trust|legit|why choose|why buy)/i.test(q)) {
      return {
        text: `**About Reicuri & Why You Can Trust Us**\n\nReicuri is a premium wellness brand built on the philosophy of **Pure Wellness. Proven Science.**\n\n✅ **Scientifically Proven:** Our flagship **Paradox Omega-3** is a patented formula developed by Dr. Hayhurst at Ulster University (UK & Ireland) and proven to be up to 70% more absorbable.\n✅ **Highest Purity:** All our products are FSSAI Approved, Non-GMO, Gluten-Free, and strictly third-party tested for purity (Heavy Metal-Free).\n✅ **Premium Ingredients:** We use Mediterranean olive oil, natural lemon oil (no fishy burps), and high-grade extracts.\n\n🏢 **Headquarters:** Bengaluru, Karnataka, India\n📞 **Support:** 98458 15772 | digital@reicuri.com\n\nWe believe in complete transparency and results you can actually feel!`,
        cards: []
      };
    }

    const result = index.search(q);
    if (result) {
      const doc = result.doc;
      if (doc.type === 'faq') {
        return {
          text: `**Q: ${doc.metadata.q}**\n\n${doc.metadata.a}`,
          cards: [doc.metadata.productId]
        };
      } else if (doc.type === 'product') {
        return {
          text: formatProductDetail(doc.metadata.product),
          cards: [doc.id]
        };
      } else if (doc.type === 'policy') {
        return {
          text: doc.metadata.content,
          cards: []
        };
      } else if (doc.type === 'goal') {
        return {
          text: `Based on your interest in ${doc.metadata.goal}, here are the products that can help:`,
          cards: doc.metadata.products
        };
      }
    }

    return { 
      text: KB.fallback, 
      options: [
        { label: "Find the Right Supplement", query: "Find the Right Supplement" },
        { label: "Browse Products", query: "Browse Products" },
        { label: "Contact Support", query: "Contact" }
      ],
      cards: [] 
    };
  }

  // Public API
  return { respond, KB, formatProductCard };
})();
